import type { CSSProperties } from 'react';

import type { QuotaStatusResponse } from '../../types';
import { busiestQuota, quotaTone } from '../../lib/quota';
import { quotaLabel } from '../../lib/accents';
import type { Tab } from '../App';
import { IconLibrary, IconSearch } from './Icons';

/**
 * Deux onglets, et non trois.
 *
 * Les rendez-vous ne sont pas un lieu séparé : ce sont des fiches de la
 * bibliothèque qui ont une date. Ils vivent donc dans le même onglet, derrière
 * un sélecteur — un troisième onglet obligeait à savoir d'avance dans lequel
 * des deux chercher une fiche dont on ne se rappelle plus le statut.
 */
const TABS: Array<{ id: Tab; label: string; Icon: typeof IconSearch }> = [
  { id: 'search', label: 'Recherche', Icon: IconSearch },
  { id: 'library', label: 'Bibliothèque', Icon: IconLibrary },
];

interface TabBarProps {
  tab: Tab;
  onTabChange: (tab: Tab) => void;
  /** Enregistrés + rendez-vous : la pastille annonce tout ce qui attend. */
  libraryCount: number;
  /**
   * Où vit cette instance : en bas de la coque, ou dans la barre haute.
   *
   * Les deux sont rendues en permanence et une media query n'en laisse qu'une
   * visible. `display: none` — et non `visibility` ni un décalage hors écran —
   * retire l'instance inactive de l'arbre d'accessibilité **et** de l'ordre de
   * tabulation : il n'y a jamais deux navigations opérables à la fois.
   *
   * Celle du bas est un élément de flux ordinaire, jamais `position: fixed` :
   * voir le long commentaire en tête d'`app.css` pour la raison.
   */
  place: 'top' | 'bottom';
}

export function TabBar({ tab, onTabChange, libraryCount, place }: TabBarProps) {
  const counts: Partial<Record<Tab, number>> = { library: libraryCount };

  return (
    <nav className={`tabbar-${place}`} aria-label="Navigation principale">
      {TABS.map(({ id, label, Icon }) => {
        const count = counts[id];
        return (
          <button
            key={id}
            type="button"
            className="tab"
            aria-current={tab === id ? 'page' : undefined}
            onClick={() => onTabChange(id)}
          >
            <Icon className="tab-icon" size={22} />
            {label}
            {count !== undefined && count > 0 ? (
              <span className="tab-badge">{count}</span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}

const numberFormat = new Intl.NumberFormat('fr-FR');

/**
 * Le compteur de quota, réduit à ce qui décide quelque chose.
 *
 * Un seul chiffre compte : celui du compteur le plus proche de son plafond,
 * parce que c'est lui qui coupera le service en premier. Le détail complet
 * reste dans l'infobulle, il n'occupe pas l'écran.
 */
export function QuotaPill({
  status,
  isLoading,
  hasError,
  onRetry,
}: {
  status: QuotaStatusResponse | null;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
}) {
  if (hasError) {
    return (
      <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
        Compteurs indisponibles
      </button>
    );
  }

  if (status === null) {
    return <span className="quota">{isLoading ? 'Chargement…' : '—'}</span>;
  }

  const worst = busiestQuota(status);
  const tone = quotaTone(worst.value.percentage);
  const fill = Math.min(100, Math.max(0, worst.value.percentage));
  const style = { transform: `scaleX(${fill / 100})` } satisfies CSSProperties;

  return (
    <span
      className={`quota quota-${tone}`}
      title={`${quotaLabel(worst.key, worst.label)} — ${numberFormat.format(worst.value.used)} sur ${numberFormat.format(worst.value.limit)} ce mois-ci. C'est le compteur le plus proche de son plafond.`}
    >
      <span className="quota-bar" aria-hidden="true">
        <span className="quota-fill" style={style} />
      </span>
      <span>
        <span className="quota-num">{numberFormat.format(worst.value.remaining)}</span>
        <span className="quota-word"> restantes</span>
      </span>
    </span>
  );
}
